import { WizardLayout } from '../../components/wizard/WizardLayout';
import { WizardStep } from '../../components/wizard/WizardStep';
import { de } from '../../lib/i18n/de';
import { useDimensionForm } from '../../components/wizard/fields/useDimensionForm';
import {
  BooleanField,
  CheckboxGroup,
  NumberField,
  TextareaField,
  type Option,
} from '../../components/wizard/fields/FormFields';
import { QualificationsEditor } from '../../components/wizard/fields/ArrayEditors';
import { IDENTITY_KEYS, parseList, serializeList } from '../../lib/store/dimensionScoreInput';
import { DimensionLiveScore } from '../../components/wizard/fields/DimensionLiveScore';

const t = de.dimensionFields.identity;

const TYPE_OPTIONS: Option[] = Object.entries(t.qualTypeOptions).map(([value, label]) => ({
  value,
  label,
}));
const TRAIT_OPTIONS: Option[] = Object.entries(t.traitOptions).map(([value, label]) => ({
  value,
  label,
}));

export function IdentityStep() {
  const form = useDimensionForm('identity');
  const traits = parseList<string>(form.get(IDENTITY_KEYS.personalTraits));
  const hasContent = Object.values(form.fields).some((v) => v.trim().length > 0);

  return (
    <WizardLayout>
      <WizardStep
        stepId="identity"
        canProceed={hasContent}
        lastSavedAt={form.lastSavedAt}
        saveState={form.lastSavedAt ? 'saved' : 'idle'}
        onProceed={form.markComplete}
      >
        <div className="space-y-8">
          <QualificationsEditor
            label={t.qualificationsLabel}
            hint={t.qualificationsHint}
            typeOptions={TYPE_OPTIONS}
            addLabel={t.qualAddLabel}
            industryRelevantLabel={t.qualIndustryRelevant}
            value={form.get(IDENTITY_KEYS.qualifications)}
            onChange={(s) => {
              form.set(IDENTITY_KEYS.qualifications, s);
            }}
          />
          <NumberField
            id="identity-experience"
            label={t.industryExperienceYearsLabel}
            suffix={t.industryExperienceYearsSuffix}
            min={0}
            value={form.get(IDENTITY_KEYS.industryExperienceYears)}
            onChange={(v) => {
              form.set(IDENTITY_KEYS.industryExperienceYears, v);
            }}
          />
          <BooleanField
            id="identity-entrepreneurial"
            label={t.hasEntrepreneurialExperienceLabel}
            required
            value={form.get(IDENTITY_KEYS.hasEntrepreneurialExperience)}
            onChange={(v) => {
              form.set(IDENTITY_KEYS.hasEntrepreneurialExperience, v);
            }}
          />
          <NumberField
            id="identity-years-self"
            label={t.yearsSelfEmployedLabel}
            suffix={t.yearsSelfEmployedSuffix}
            min={0}
            max={80}
            value={form.get(IDENTITY_KEYS.yearsSelfEmployed)}
            onChange={(v) => {
              form.set(IDENTITY_KEYS.yearsSelfEmployed, v);
            }}
          />
          <TextareaField
            id="identity-usp"
            label={t.uspLabel}
            hint={t.uspHint}
            placeholder={t.uspPlaceholder}
            helpKey="identity.qualification"
            minLength={50}
            value={form.get(IDENTITY_KEYS.usp)}
            onChange={(v) => {
              form.set(IDENTITY_KEYS.usp, v);
            }}
          />
          <CheckboxGroup
            label={t.personalTraitsLabel}
            hint={t.personalTraitsHint}
            options={TRAIT_OPTIONS}
            selected={traits}
            onToggle={(value, next) => {
              const updated = next ? [...traits, value] : traits.filter((x) => x !== value);
              form.set(IDENTITY_KEYS.personalTraits, serializeList(updated));
            }}
          />
          <TextareaField
            id="identity-notes"
            label={de.dimensionFields.common.notesLabel}
            hint={de.dimensionFields.common.notesHint}
            placeholder={de.dimensionFields.common.notesPlaceholder}
            rows={3}
            value={form.get(IDENTITY_KEYS.notes)}
            onChange={(v) => {
              form.set(IDENTITY_KEYS.notes, v);
            }}
          />
          <DimensionLiveScore dimension="identity" fields={form.fields} />
        </div>
      </WizardStep>
    </WizardLayout>
  );
}
